import * as XLSX from 'xlsx';
import { IDelayQuestions } from '../../../shared/types/questions';
import { DEFAULT_LANGUAGE, Language, translations } from '../../../shared/i18n/translations';
import { sanitizeSpreadsheetRow } from '../../../shared/helpers/spreadsheet-security';

interface ResponseData {
    data: IDelayQuestions[];
    total: number;
    filters: {
        companyId: string;
        startDate: string | null;
        endDate: string | null;
    };
    period: {
        start: string | null;
        end: string | null;
    };
}

const getReportText = (language: Language, key: string) => {
    return translations[language].reports[key] || key;
};

const replaceTokens = (text: string, values: Record<string, string>) => {
    return Object.entries(values).reduce(
        (result, [key, value]) => result.replaceAll(`{${key}}`, value),
        text
    );
};

const formatDateTime = (dateString: string | null | undefined, language: Language): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleString(language === 'en' ? 'en-US' : 'pt-BR');
};

const translateChoice = (choice: string | null | undefined, language: Language): string => {
    if (!choice) return '';
    
    const translations: { [key: string]: string } = {
        immediate: getReportText(language, 'immediateChoice'),
        delayed: getReportText(language, 'delayedChoice'),
        Immediate: getReportText(language, 'immediateChoice'),
        Delayed: getReportText(language, 'delayedChoice')
    };
    
    return translations[choice] || choice;
};

export const ExportDelayData = (response: ResponseData, fileName: string, language: Language = DEFAULT_LANGUAGE) => {
    try {
        const excelData = response.data.map((item) => sanitizeSpreadsheetRow({
            [getReportText(language, 'recordId')]: item.record_id,
            [getReportText(language, 'participantName')]: item.nome,
            [getReportText(language, 'birthDate')]: item.dtnasc,
            [getReportText(language, 'applicator')]: item.applicatorName || getReportText(language, 'notAvailable'),
            [getReportText(language, 'immediateValue')]: item.valor_imediato,
            [getReportText(language, 'delayedValue')]: item.valor_atrasado,
            [replaceTokens(getReportText(language, 'responseIndex'), { number: '1' })]: item.r1_indice,
            [replaceTokens(getReportText(language, 'responseTime'), { number: '1' })]: item.r1_tempo,
            [replaceTokens(getReportText(language, 'responseChoice'), { number: '1' })]: translateChoice(item.r1_escolha, language),
            [replaceTokens(getReportText(language, 'responseIndex'), { number: '2' })]: item.r2_indice,
            [replaceTokens(getReportText(language, 'responseTime'), { number: '2' })]: item.r2_tempo,
            [replaceTokens(getReportText(language, 'responseChoice'), { number: '2' })]: translateChoice(item.r2_escolha, language),
            [replaceTokens(getReportText(language, 'responseIndex'), { number: '3' })]: item.r3_indice,
            [replaceTokens(getReportText(language, 'responseTime'), { number: '3' })]: item.r3_tempo,
            [replaceTokens(getReportText(language, 'responseChoice'), { number: '3' })]: translateChoice(item.r3_escolha, language),
            [replaceTokens(getReportText(language, 'responseIndex'), { number: '4' })]: item.r4_indice,
            [replaceTokens(getReportText(language, 'responseTime'), { number: '4' })]: item.r4_tempo,
            [replaceTokens(getReportText(language, 'responseChoice'), { number: '4' })]: translateChoice(item.r4_escolha, language),
            [replaceTokens(getReportText(language, 'responseIndex'), { number: '5' })]: item.r5_indice,
            [replaceTokens(getReportText(language, 'responseTime'), { number: '5' })]: item.r5_tempo,
            [replaceTokens(getReportText(language, 'responseChoice'), { number: '5' })]: translateChoice(item.r5_escolha, language),
            'ED50': item.ed50,
            'K': item.k,
            [getReportText(language, 'startedAt')]: formatDateTime(item.startedAt, language),
            [getReportText(language, 'completedAt')]: formatDateTime(item.completedAt, language),
            [getReportText(language, 'totalTimeSeconds')]: item.totalTimeSeconds,
        }));

        const documentationData = [
            {
                [getReportText(language, 'field')]: getReportText(language, 'recordId'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'recordIdDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'participantName'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'participantNameDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'birthDate'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'birthDateDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'applicator'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'applicatorDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'immediateValue'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'immediateValueDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'delayedValue'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'delayedValueDescription')
            },
            {
                [getReportText(language, 'field')]: 'ED50',
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'ed50Description')
            },
            {
                [getReportText(language, 'field')]: 'K',
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'kDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'startedAt'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'startedAtDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'completedAt'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'completedAtDescription')
            },
            {
                [getReportText(language, 'field')]: getReportText(language, 'totalTimeSeconds'),
                [getReportText(language, 'fieldDescription')]: getReportText(language, 'totalTimeDescription')
            }
        ];

        for (let index = 1; index <= 5; index += 1) {
            documentationData.splice(6 + ((index - 1) * 3), 0,
                {
                    [getReportText(language, 'field')]: replaceTokens(getReportText(language, 'responseIndex'), { number: String(index) }),
                    [getReportText(language, 'fieldDescription')]: replaceTokens(getReportText(language, 'responseIndexDescription'), { number: String(index) })
                },
                {
                    [getReportText(language, 'field')]: replaceTokens(getReportText(language, 'responseTime'), { number: String(index) }),
                    [getReportText(language, 'fieldDescription')]: replaceTokens(getReportText(language, 'responseTimeDescription'), { number: String(index) })
                },
                {
                    [getReportText(language, 'field')]: replaceTokens(getReportText(language, 'responseChoice'), { number: String(index) }),
                    [getReportText(language, 'fieldDescription')]: replaceTokens(getReportText(language, 'responseChoiceDescription'), { number: String(index) })
                }
            );
        }

        // Criar as planilhas
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const docWorksheet = XLSX.utils.json_to_sheet(documentationData);

        // Ajustar larguras das colunas - Aba de Dados
        const columnWidths = [
            { wch: 15 },  // Record ID
            { wch: 25 },  // Nome
            { wch: 18 },  // Data Nascimento
            { wch: 25 },  // Aplicador (novo campo)
            { wch: 15 },  // Valor Imediato
            { wch: 15 },  // Valor Atrasado
            { wch: 12 },  // R1 Índice
            { wch: 14 },  // R1 Tempo (s)
            { wch: 14 },  // R1 Escolha
            { wch: 12 },  // R2 Índice
            { wch: 14 },  // R2 Tempo (s)
            { wch: 14 },  // R2 Escolha
            { wch: 12 },  // R3 Índice
            { wch: 14 },  // R3 Tempo (s)
            { wch: 14 },  // R3 Escolha
            { wch: 12 },  // R4 Índice
            { wch: 14 },  // R4 Tempo (s)
            { wch: 14 },  // R4 Escolha
            { wch: 12 },  // R5 Índice
            { wch: 14 },  // R5 Tempo (s)
            { wch: 14 },  // R5 Escolha
            { wch: 15 },  // ED50
            { wch: 15 },  // K
            { wch: 22 },  // Iniciado em
            { wch: 22 },  // Completado em
            { wch: 16 },  // Tempo Total (s)
        ];
        worksheet['!cols'] = columnWidths;

        // Ajustar larguras das colunas - Aba de Documentação
        const docColumnWidths = [
            { wch: 20 },  // Campo
            { wch: 120 }, // Descrição
        ];
        docWorksheet['!cols'] = docColumnWidths;

        // Criar o workbook e adicionar as abas
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, getReportText(language, 'dataSheet'));
        XLSX.utils.book_append_sheet(workbook, docWorksheet, getReportText(language, 'documentationSheet'));

        const periodInfo = response.period.start && response.period.end 
            ? `_${response.period.start.split('T')[0]}_${getReportText(language, 'filePeriodSeparator')}_${response.period.end.split('T')[0]}`
            : `_${new Date().toISOString().split('T')[0]}`;
        
        const fileNameWithDate = `${fileName}${periodInfo}.xlsx`;
        
        XLSX.writeFile(workbook, fileNameWithDate);
        
        console.log(`✅ Relatório Excel exportado com sucesso: ${fileNameWithDate}`);
        console.log(`📊 Total de registros exportados: ${response.total}`);
        console.log(`📄 Abas criadas: Dados e Documentação`);
        
        return true;
        
    } catch (error) {
        console.error('❌ Erro ao exportar dados para Excel:', error);
        throw error;
    }
};
